import { normalizeAspectRatio } from '../rendering/scene-design-tile-strip';
import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../types';

import { PreviewSceneDesignsTool } from './PreviewSceneDesignsTool';
import {
    formatDesignCatalog,
    getBrowsableSceneDesigns,
    PREVIEW_SCENE_DESIGNS_CAP,
} from './sceneDesignBrowsing';

import { getDisplayGroupById } from 'js/src/data/templates/templates-selectors';

/**
 * Lists the current template's scene designs — the professionally-made layouts that define the
 * template's look — as a cheap, text-only catalog (no rendering). It's the first half of a
 * two-step flow: the agent scans this list, then previews a few relevant ids with
 * preview_scene_designs to see how they look and how they're built.
 */
export class ListSceneDesignsTool extends Tool {
    public static readonly toolName = 'list_scene_designs';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: ListSceneDesignsTool.toolName,
                description:
                    "List the current template's scene designs — the professionally-made layouts " +
                    "that define the template's visual language — as a cheap, text-only catalog " +
                    '(id, name, group; no images). Reach for this when the built-in scene context ' +
                    "isn't enough design direction: the graphic's scene has no useful neighbors " +
                    '(only scene, first/last, or a full-bleed media neighbor), or you want to ' +
                    'match the template more closely or need inspiration. This is step 1 of 2 — ' +
                    `scan the list, then call ${PreviewSceneDesignsTool.toolName} with a few (up to ${PREVIEW_SCENE_DESIGNS_CAP}) ` +
                    'relevant ids to render their previews and see how they are built. Scoped to ' +
                    'the current template and aspect ratio automatically.',
                parameters: { type: 'object', properties: {}, required: [] },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Listing scene designs';
    }

    public get statusIcon(): string {
        return 'view_list';
    }

    public async execute(
        _args: Record<string, never>,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const templateId = this.luminary.template?.id;
        if (!templateId) {
            return {
                result: 'Error: no template is loaded, so there are no scene designs to list.',
                isSuccess: false,
            };
        }

        const state = this.getState();
        const aspectRatio = normalizeAspectRatio(this.luminary.aspectRatio);
        const designs = getBrowsableSceneDesigns(state, templateId, aspectRatio);
        if (!designs.length) {
            return {
                result: 'No scene designs found in this template.',
                isSuccess: true,
            };
        }

        const getGroupName = (groupId: number): string =>
            getDisplayGroupById(state, groupId)?.get('display_name') ?? '';

        return { result: formatDesignCatalog(designs, getGroupName), isSuccess: true };
    }
}
